import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, User, Menu, X, LogOut, ChevronDown, Package, Heart, MapPin, Settings, MessageCircle, Search } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import useCartStore from '../store/cartStore';
import useWishlistStore from '../store/wishlistStore';
import AuthModal from './AuthModal';
import { toast } from 'sonner';

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [user, setUser] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const itemCount = useCartStore(state => state.getItemCount());
  const wishlistCount = useWishlistStore(state => state.getCount());

  const isHomepage = location.pathname === '/';

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (userStr && token) {
      try {
        setUser(JSON.parse(userStr));
      } catch (e) {
        console.error('Failed to parse user data');
      }
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setShowUserMenu(false);
    toast.success('Signed out successfully');
  };

  const handleAuthSuccess = (userData) => {
    setUser(userData);
  };

  const navBackground = scrolled || !isHomepage
    ? 'bg-[#FAF9F6]/98 backdrop-blur-xl shadow-sm'
    : 'bg-transparent';

  const textColor = scrolled || !isHomepage ? 'text-[#1A1A1A]' : 'text-white';
  const logoColor = scrolled || !isHomepage ? 'text-[#1A1A1A]' : 'text-white';

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Clothing', path: '/products' },
    { name: 'Footwear', path: '/products' },
    { name: 'Plus Size', path: '/products' },
    { name: 'New Arrivals', path: '/products' },
    { name: 'Offers', path: '/products' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBackground}`}
        data-testid="main-navbar"
      >
        {/* Top Bar - Icons */}
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-14 md:h-16 border-b border-white/10">
            {/* Left Icons - Menu (Mobile) */}
            <div className="flex items-center gap-3">
              <button 
                className={`lg:hidden p-2 ${textColor}`}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <button className={`hidden md:block p-2 ${textColor} hover:text-[#C59746] transition-colors`}>
                <Search className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            {/* Center Logo */}
            <Link to="/" className="flex flex-col items-center" data-testid="brand-logo">
              <span className={`font-heading text-xl md:text-2xl lg:text-3xl font-light tracking-wide ${logoColor} transition-colors duration-300`}>
                JAT FASHION HOUSE
              </span>
              <span className={`text-[9px] md:text-[10px] tracking-[0.25em] uppercase ${scrolled || !isHomepage ? 'text-[#C59746]' : 'text-[#C59746]'} font-medium`}>
                Wear Your Identity
              </span>
            </Link>

            {/* Right Icons */}
            <div className="flex items-center gap-2 md:gap-3">
              {/* Account */}
              <div className="relative" ref={userMenuRef}>
                {user ? (
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className={`p-2 ${textColor} hover:text-[#C59746] transition-colors`}
                    data-testid="user-menu-btn"
                  >
                    <User className="w-5 h-5" strokeWidth={1.5} />
                  </button>
                ) : (
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className={`p-2 ${textColor} hover:text-[#C59746] transition-colors`}
                    data-testid="login-btn"
                  >
                    <User className="w-5 h-5" strokeWidth={1.5} />
                  </button>
                )}
                
                {/* User Dropdown */}
                {showUserMenu && user && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white shadow-xl border border-[#1A1A1A]/10 py-2 z-50">
                    <div className="px-4 py-3 border-b border-[#1A1A1A]/10">
                      <p className="text-sm font-medium text-[#1A1A1A]">{user.name || 'User'}</p>
                      <p className="text-xs text-[#1A1A1A]/60">{user.email}</p>
                    </div>
                    <Link to="/account" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#FAF9F6]" onClick={() => setShowUserMenu(false)}>
                      <Package className="w-4 h-4" /> My Orders
                    </Link>
                    <Link to="/account/profile" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#FAF9F6]" onClick={() => setShowUserMenu(false)}>
                      <Settings className="w-4 h-4" /> Profile
                    </Link>
                    <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">
                      <LogOut className="w-4 h-4" /> Sign Out
                    </button>
                  </div>
                )}
              </div>
              
              {/* Wishlist */}
              <Link 
                to="/wishlist" 
                className={`hidden md:block relative p-2 ${textColor} hover:text-[#C59746] transition-colors`}
                data-testid="wishlist-nav-btn"
              >
                <Heart className="w-5 h-5" strokeWidth={1.5} />
                {wishlistCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                    {wishlistCount}
                  </span>
                )}
              </Link>
              
              {/* Cart */}
              <Link 
                to="/cart" 
                className={`relative p-2 ${textColor} hover:text-[#C59746] transition-colors`}
                data-testid="cart-button"
              >
                <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
                {itemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#C59746] text-white text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                    {itemCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>

        {/* Navigation Menu - Desktop */}
        <div className={`hidden lg:block border-b ${scrolled || !isHomepage ? 'border-[#1A1A1A]/5' : 'border-white/10'}`}>
          <div className="max-w-[1600px] mx-auto px-8">
            <div className="flex items-center justify-center gap-10 h-12">
              {navLinks.map((link) => (
                <Link 
                  key={link.name}
                  to={link.path}
                  className={`text-[11px] font-medium tracking-[0.15em] uppercase ${textColor} hover:text-[#C59746] transition-colors duration-300`}
                  data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div 
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          mobileMenuOpen ? 'visible' : 'invisible'
        }`}
      >
        <div 
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileMenuOpen(false)}
        />
        <div 
          className={`absolute top-0 left-0 bottom-0 w-[280px] bg-[#FAF9F6] transform transition-transform duration-300 ${
            mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="p-6 border-b border-[#1A1A1A]/10">
            <span className="font-heading text-xl font-light text-[#1A1A1A]">JAT FASHION HOUSE</span>
          </div>
          <div className="py-4">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="block px-6 py-3 text-sm tracking-wider uppercase text-[#1A1A1A] hover:bg-[#C59746]/10 hover:text-[#C59746] transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-[#1A1A1A]/10">
            <a 
              href="https://wa.me/919328566392"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[#25D366] text-white py-3 text-sm font-medium rounded-full"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp Us
            </a>
          </div>
        </div>
      </div>

      {/* Spacer for fixed navbar */}
      <div className="h-[104px] lg:h-[112px]" />

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
    </>
  );
};

export default Navbar;
